import React from 'react';

import config from '../config';
import { trackEvent } from './events-tracker-service';
import { getCurrentPlan, getActiveProjects, getNonGuestTeamUsers, getGuestTeamUsers } from './store-service';
import { showModal } from '../actions/modal';
import { createAlert } from '../services/alert-service';
import { convertBytesToHumanReadable } from '../utils/file';
import { getPlanById } from '../utils/plan';
import { featureAvailable } from '../services/features-service';
import { browserHistory } from '../services/history';
import { canUser } from '../services/permissions-service';

import FieldUser from '../components/field-user';

const handleMembersUpgradeClick = event => {
    event.preventDefault();
    handleUpgrade('members');
}

const handleUpgradeTrigger = type => {
    trackEvent('PLAN_QUOTA_REACHED.TRIGGER', {
        type,
        billingEnabled: config.BILLING_ENABLED
    });
}

const handleUpgrade = type => {
    trackEvent('PLAN_QUOTA_REACHED_UPGRADE.CLICK', { type });

    if (getCurrentTeamPlan().appSumo) {
        browserHistory.push({
            pathname: `/settings/team/current/billing`,
            state: {
                closeUrl: browserHistory.getCurrentLocation().pathname
            } 
        });

        return;
    }

    showModal('SWITCH_PLAN');
}

const getCurrentTeamPlan = () => getPlanById(getCurrentPlan());

const UpgradeButton = ({ uppercase, onClick }) => {
    if (canUser('editBilling')) {
        return <a href="" onClick={onClick}><strong><u>{uppercase ? 'U' : 'u'}pgrade</u></strong></a>;
    }

    return `${uppercase ? 'C' : 'c'}ontact your team owner to upgrade`;
}

export const canCreateProject = showAlert => {
    const plan = getCurrentTeamPlan();

    if (!plan || !plan.quota || !plan.quota.projects) {
        return true;
    }

    if (plan.quota.projects > getActiveProjects().length) {
        return true;
    }

    if (showAlert) {
        handleUpgradeTrigger('projects');

        if (config.BILLING_ENABLED) {
            const clickHandler = event => {
                event.preventDefault();
                alert.hide();
                handleUpgrade('projects');
            }
            const alert = createAlert('default', <div><i className="icon material-icons">error</i> {plan.name} plan doesn’t allow more than {plan.quota.projects} projects. Please <UpgradeButton onClick={clickHandler} /> to create unlimited number of projects.</div>, {
                duration: 7000
            });
        } else {
            createAlert('default', <div><i className="icon material-icons">error</i> {plan.name} plan doesn’t allow more than {plan.quota.projects} projects.</div>, {
                duration: 7000
            });
        }
    }

    return false;
}

export const canInviteGuests = showAlert => {
    if (featureAvailable('inviteGuests')) {
        return true;
    }

    if (showAlert) {
        handleUpgradeTrigger('clients');

        if (config.BILLING_ENABLED) {
            const clickHandler = event => {
                event.preventDefault();
                alert.hide();
                handleUpgrade('clients');
            }
            const alert = createAlert('default', <div><i className="icon material-icons">error</i> <UpgradeButton onClick={clickHandler} uppercase={true} /> account to invite Guests into projects.</div>, {
                duration: 7000
            });
        } else {
            createAlert('default', <div><i className="icon material-icons">error</i> Upgrade account to invite Guests into projects.</div>, {
                duration: 7000
            });
        }
    }

    return false;
}

export const canTrackTime = showAlert => {
    if (featureAvailable('timeTracking')) {
        return true;
    }

    if (showAlert) {
        handleUpgradeTrigger('timeTracking');

        if (config.BILLING_ENABLED) {
            const clickHandler = event => {
                event.preventDefault();
                alert.hide();
                handleUpgrade('timeTracking');
            }
            const alert = createAlert('default', <div><i className="icon material-icons">error</i> <UpgradeButton onClick={clickHandler} uppercase={true} /> your account now to enable time tracking and reporting.</div>, {
                duration: 7000
            });
        } else {
            createAlert('default', <div><i className="icon material-icons">error</i> Upgrade your account now to enable time tracking and reporting.</div>, {
                duration: 7000
            });
        }
    }

    return false;
}

export const getRemainingTeamMembers = () => {
    const plan = getCurrentTeamPlan();

    if (!plan || !plan.quota || !('members' in plan.quota)) {
        return Number.POSITIVE_INFINITY;
    }

    const teamMembersCount = getNonGuestTeamUsers().length;
    return plan.quota.members - teamMembersCount;
}

export const getRemainingClients = () => {
    const plan = getCurrentTeamPlan();

    if (!plan || !plan.quota || !('clients' in plan.quota)) {
        return Number.POSITIVE_INFINITY;
    }

    const guestsCount = getGuestTeamUsers().length;
    return plan.quota.clients - guestsCount;
}

export const NoMoreMembersAllowed = ({ singleLine, maxFieldsCount, onAddExistingEnable, clients }) => {
    const plan = getCurrentTeamPlan();

    if (!plan) {
        return null;
    }

    const quota = plan.quota[clients ? 'clients' : 'members'];

    const handleAddExistingClick = event => {
        event.preventDefault();

        onAddExistingEnable();
    }

    handleUpgradeTrigger('members');

    return (
        <div className="field-user-list-alert">
            <div className="field-user-list-alert-bg">
                <FieldUser disabled={true} key={1} />

                {
                    !singleLine ? <FieldUser disabled={true} key={2} /> : null
                }
            </div>

            <div className="field-user-list-alert-message">
                {
                    config.BILLING_ENABLED ? (
                        <p>
                            <UpgradeButton onClick={handleMembersUpgradeClick} uppercase={true} />

                            &nbsp;to invite {quota === 0 ? '' : ' more'} {clients ? ' guests' : ' team members'}

                            {
                                !clients && onAddExistingEnable ? (
                                    <React.Fragment>
                                        &nbsp;or <a href="" onClick={handleAddExistingClick}><strong>Add Existing Members</strong></a>.
                                    </React.Fragment>
                                ) : null
                            }
                        </p>

                    ) : quota ? (
                        <p>{plan.name} plan doesn’t allow more than {plan.quota.members} {clients ? ' guests' : ' team members'} <i className="ion ion-information-circled" /></p>
                    ) : (
                        <p>{plan.name} plan doesn’t allow {clients ? ' guests' : ' team members'} <i className="ion ion-information-circled" /></p>
                    )
                }
            </div>
        </div>
    )
}

export const getMaxFileSize = () => {
    const plan = getCurrentTeamPlan();

    if (!plan || !plan.quota || !plan.quota.fileBytes) {
        return Number.POSITIVE_INFINITY;
    }

    return plan.quota.fileBytes;
}

export const showMaxFileSizeError = file => {
    const plan = getCurrentTeamPlan();

    handleUpgradeTrigger('fileSize');

    if (config.BILLING_ENABLED) {
        const clickHandler = event => {
            event.preventDefault();
            alert.hide();
            handleUpgrade('fileSize');
        }
        const alert = createAlert('default', <div><i className="icon material-icons">error</i> The file you’re trying to upload exceeds a {convertBytesToHumanReadable(plan.quota.fileBytes)} limit. Please <UpgradeButton onClick={clickHandler} /> to upload files of any size.</div>, {
            duration: 7000
        });
    } else {
        createAlert('default', <div><i className="icon material-icons">error</i> {plan.name} plan doesn’t allow files larger than {convertBytesToHumanReadable(plan.quota.fileBytes)}.</div>, {
            duration: 7000
        });
    }
}

export const showStorageError = file => {
    const plan = getCurrentTeamPlan();

    handleUpgradeTrigger('storage');

    if (config.BILLING_ENABLED) {
        const clickHandler = event => {
            event.preventDefault();
            alert.hide();
            handleUpgrade('storage');
        }

        const alert = createAlert('default', <div><i className="icon material-icons">error</i> Your team reached a {convertBytesToHumanReadable(plan.quota.storageBytes)} file storage limit. Please <UpgradeButton onClick={clickHandler} /> to increase your storage.</div>, {
            duration: 7000
        });
    } else {
        createAlert('default', <div><i className="icon material-icons">error</i> {plan.name} plan doesn’t allow more than {convertBytesToHumanReadable(plan.quota.storageBytes)} file storage.</div>, {
            duration: 7000
        });
    }
}
